import { useCallback } from 'react';

import { AxiosResponse } from 'axios';
import { useInfiniteQuery, UseInfiniteQueryResult } from 'react-query';
import { PaginatedDiscountListList, UrlParams } from 'types';
import { client } from 'utils/api-client';
import { encodeUrlParams, urlParamsFromUrl } from 'utils/functions';

interface QueryKeyType {
  pageParam?: UrlParams;
}

interface IUseDiscounts {
  search?: string;
  ordering?: string;
  isActive?: boolean;
  isInactive?: boolean;
  isArchived?: boolean;
  limit?: string;
}

function useDiscounts({
  search,
  ordering,
  isActive,
  isInactive,
  isArchived,
  limit = '20',
}: IUseDiscounts): UseInfiniteQueryResult<AxiosResponse<PaginatedDiscountListList>> {
  const fetchDiscounts = useCallback(
    ({
      pageParam = { limit, offset: '0' },
    }: QueryKeyType): Promise<AxiosResponse<PaginatedDiscountListList>> => {
      const params = pageParam;

      if (search) {
        params.search = search;
      }

      if (ordering) {
        const snakeOrdering = ordering.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        params.ordering = `-deleted_at,-is_active,${snakeOrdering},pk`;
      }

      if (isActive) params.is_active = 'true';
      if (isInactive) params.is_inactive = 'true';
      if (isArchived) params.is_archived = 'true';

      return client.get(`discounts/?${encodeUrlParams(params)}`);
    },
    [limit, search, ordering, isActive, isInactive, isArchived],
  );

  return useInfiniteQuery(
    ['discounts', search, ordering, isActive, isInactive, isArchived],
    fetchDiscounts,
    {
      getNextPageParam: page => {
        return page.data.next ? urlParamsFromUrl(page.data.next) : undefined;
      },
    },
  );
}

export default useDiscounts;
